import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  cancelMassive,
  cancelMassivePackage,
  createMassive,
  fetchMassive,
  fetchMassiveErrors,
  fetchMassiveList,
  fetchMassivePackages,
  MassiveListFilters,
} from 'services/MassiveService';

const onDemand = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export const massiveKeys = {
  all: ['sega-massive'] as const,
  list: (filters: MassiveListFilters) =>
    [...massiveKeys.all, 'list', filters] as const,
  detail: (id: string) => [...massiveKeys.all, 'detail', id] as const,
  packages: (id: string, search: string, page: number) =>
    [...massiveKeys.all, 'packages', id, search, page] as const,
  errors: (id: string) => [...massiveKeys.all, 'errors', id] as const,
};

export const useMassiveList = (filters: MassiveListFilters) =>
  useQuery({
    queryKey: massiveKeys.list(filters),
    queryFn: () => fetchMassiveList(filters),
    placeholderData: keepPreviousData,
    ...onDemand,
  });

export const useMassive = (massiveId?: string) =>
  useQuery({
    queryKey: massiveKeys.detail(massiveId ?? ''),
    queryFn: () => fetchMassive(massiveId as string),
    enabled: !!massiveId,
    ...onDemand,
  });

export const PACKAGES_PAGE_SIZE = 10;

export const useMassivePackages = (massiveId?: string, search = '', page = 0) =>
  useQuery({
    queryKey: massiveKeys.packages(massiveId ?? '', search, page),
    queryFn: () =>
      fetchMassivePackages(
        massiveId as string,
        search,
        page,
        PACKAGES_PAGE_SIZE,
      ),
    enabled: !!massiveId,
    placeholderData: keepPreviousData,
    ...onDemand,
  });

export const useMassiveErrors = (massiveId?: string) =>
  useQuery({
    queryKey: massiveKeys.errors(massiveId ?? ''),
    queryFn: () => fetchMassiveErrors(massiveId as string),
    enabled: !!massiveId,
    ...onDemand,
  });

export const useCreateMassive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMassive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: massiveKeys.all });
    },
  });
};

export const useCancelMassive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      massiveId,
      keepBlocked,
    }: {
      massiveId: string;
      keepBlocked?: boolean;
    }) => cancelMassive(massiveId, keepBlocked),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: massiveKeys.all });
    },
  });
};

export const useCancelMassivePackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      massiveId,
      tracking,
    }: {
      massiveId: string;
      tracking: string;
    }) => cancelMassivePackage(massiveId, tracking),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: massiveKeys.all });
    },
  });
};
